import React, { useCallback, useMemo } from 'react';
import { TFunction } from 'i18next';
import { IMonitoringToolkit, ITabs } from '@components-clean/core/monitoring-toolkit/interface';
import { IUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import MonitoringToolkitWrapper, {
    MonitoringToolkitWrapperProps,
} from '@pages-clean/central-monitoring/monitoring-toolkit/wrapper';
import SMActiveReviewTab from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab';
import {
    SMActiveReviewProvider,
    useSMActiveReviewContext,
} from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import SubjectMonitoringReviewGrid from '@pages-clean/subject-monitoring/components/monitoring-toolkit/subject-monitoring-review-grid';
import SubjectMonitoringTagGrid from '@pages-clean/subject-monitoring/components/monitoring-toolkit/subject-monitoring-tag-grid';
import SubjectObservationsGrid from '@pages-clean/subject-monitoring/components/monitoring-toolkit/subject-observations-grid';
import LocalStorage from '@utils-clean/local-storage-service';
import { IUser, Permissions } from '@contexts/user-context';
import Constants from '@utils/constants';
import Modules from '@config/modules';

export const subjectMonitoringToolkitHelper: IMonitoringToolkit = {
    TOOLKIT_STATE_KEY: 'SubjectMonitoringToolKit.OpenState',

    BOTTOM_PANEL_MIN_SIZE: 100,

    COLLAPSED_SIZE: '0fr',

    AUTO_SIZE: '1fr',

    translationKey: 'sm',

    canUserViewToolkit(user: IUser, moduleId: number) {
        return (
            (user?.hasModule?.(Modules.SubjectMonitoring) &&
                user?.can?.({
                    module: moduleId,
                    element: Constants.PageElements.SubjectMonitoringToolkit,
                    permission: Permissions.Enabled,
                })) ??
            false
        );
    },

    canEditToolkit(user: IUser) {
        return (
            user?.canWrite?.({
                module: Constants.Modules.SM,
                element: Constants.DataElements.SubjectMonitoring,
                permission: Permissions.ReadWrite,
            }) ?? false
        );
    },

    tabsContent() {
        return [];
    },

    clearMonitoringToolKitOpenState(user: IUser) {
        LocalStorage.removeItem(user, this.TOOLKIT_STATE_KEY);
    },

    hasUnsavedChanges(unsavedChangesContext: IUnsavedChangesContext) {
        return unsavedChangesContext.anySubjectMonitoringToolkitHasChanges();
    },

    clearUnsavedChanges(unsavedChangesContext: IUnsavedChangesContext) {
        unsavedChangesContext.subjectMonitoringToolkitDiscardChanges();
    },
};

type Props = Omit<MonitoringToolkitWrapperProps, 'toolkitHelper'>;

export const SubjectMonitoringToolkit = (props: Props) => {
    const smActiveReviewContext = useSMActiveReviewContext();

    const tabsContent: IMonitoringToolkit['tabsContent'] = useCallback(
        (
            organisationKey: string,
            studyRef: string,
            studyNumber: string,
            canEdit: boolean,
            isHalfScreen: boolean,
            _datasetId: number,
            setSelectedTab,
            t: TFunction,
        ) => {
            const tabs: (ITabs & { visible?: boolean })[] = [
                {
                    title: t('subjectObservations'),
                    shortTitle: t('subjectObservationsShort'),
                    content: (
                        <SubjectObservationsGrid
                            orgKey={organisationKey}
                            studyRef={studyRef}
                            studyNumber={studyNumber}
                            canEdit={canEdit}
                            isHalfScreen={isHalfScreen}
                        />
                    ),
                },
                {
                    title: t('allReviews'),
                    shortTitle: t('allReviewsShort'),
                    content: (
                        <SubjectMonitoringReviewGrid
                            canEdit={canEdit}
                            studyRef={studyRef}
                            enterReviewMode={async (reviewRecordId: number, sessionId: string) => {
                                smActiveReviewContext.dispatch({
                                    type: 'SET_ACTIVE_REVIEW',
                                    payload: {
                                        reviewRecordId,
                                        sessionId,
                                    },
                                });
                                await setSelectedTab(3, true);
                            }}
                        />
                    ),
                },
                {
                    title: t('manageTags'),
                    shortTitle: t('manageTagsShort'),
                    content: <SubjectMonitoringTagGrid canEdit={canEdit} studyRef={studyRef} />,
                },
                {
                    title: t('activeReview'),
                    shortTitle: t('activeReviewShort'),
                    content: (
                        <>
                            {smActiveReviewContext.state.reviewRecordId != null && (
                                <SMActiveReviewTab
                                    isHalfScreen={isHalfScreen}
                                    studyRef={studyRef}
                                    reviewRecordId={smActiveReviewContext.state.reviewRecordId}
                                    sessionId={smActiveReviewContext.state.sessionId}
                                    exitReviewMode={async () => {
                                        await setSelectedTab(1, true);
                                        smActiveReviewContext.dispatch({ type: 'CLEAR_ACTIVE_REVIEW' });
                                    }}
                                />
                            )}
                        </>
                    ),
                    tabHeaderClassName: 'sm-active-review-tab',
                    tabContentClassName: 'sm-active-review-tab',
                    visible: smActiveReviewContext.state.reviewRecordId != null,
                },
                // Include tabs where `visible` is either true or undefined
            ];

            return tabs.filter((tab) => tab.visible !== false);
        },
        [smActiveReviewContext],
    );

    const toolkitHelper: IMonitoringToolkit = useMemo(
        () => ({
            ...subjectMonitoringToolkitHelper,
            tabsContent,
        }),
        [tabsContent],
    );

    return <MonitoringToolkitWrapper {...props} toolkitHelper={toolkitHelper} />;
};

export const SubjectMonitoringToolkitContexts = ({
    studyRef,
    children,
}: {
    studyRef: string;
    children?: React.ReactNode;
}) => {
    return <SMActiveReviewProvider studyRef={studyRef}>{children}</SMActiveReviewProvider>;
};
